import { observer } from 'mobx-react-lite';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
// Screens
import { AuthHome, AuthRegister, AuthLogin, AuthLayout } from 'screens/Auth';
import { Profile, Layout, LandlordVerification } from 'screens/Common';
import {
  NoticesLayout,
  NoticesAdmin,
  NoticesPayments,
  NoticesMessaging,
} from 'screens/Notices';
import { PaymentsHome, PaymentsEditBankAccount } from 'screens/Banking';
import Payments from 'screens/Payments';
import { SubscriptionHome } from 'screens/Subscription';
import {
  PropertiesLayout,
  PropertiesList,
  PropertiesView,
  PropertiesViewLayout,
  PropertiesCreate,
  PropertiesViewManage,
  PropertiesViewTenancies,
  PropertiesViewTenancy,
  PropertiesViewTenancyLayout,
  PropertiesViewTenancyNotices,
  PropertiesViewTenancyPayments,
  PropertiesViewTenancyDetails,
  PropertiesCreateTenancy,
  PropertiesViewTenancyToDo,
  PropertiesViewToDo,
} from 'screens/Properties';
import { Search } from 'screens/Search';
import {
  TenanciesLayout,
  TenanciesList,
  TenanciesListPast,
  TenanciesViewLayout,
  TenanciesView,
  TenanciesViewTodo,
  TenanciesViewDetails,
  TenanciesViewPayments,
  TenanciesViewNotices,
} from 'screens/Tenancies';

import { useProfile, usePublicAccount, useRootState } from 'state';
import { Modal } from 'components';
import { profileTypes, verificationStatusTypes } from 'config/types';
import AuthAppSwitch from 'lib/AuthAppSwitch';

const AuthApp = () => (
  <Routes>
    <Route path="*" element={<AuthLayout />}>
      <Route index element={<AuthHome />} />
      <Route path="login" element={<AuthLogin />} />
      <Route path="registration" element={<AuthRegister />} />
      <Route path="*" element={<Navigate to="" replace />} />
    </Route>
  </Routes>
);

const MainApp = observer(() => {
  const navigate = useNavigate();
  const profile = useProfile();
  const publicAccount = usePublicAccount();
  const { profileType } = profile.data;
  const isLandlord =
    profileType === profileTypes.LANDLORD ||
    profileType === profileTypes.MANAGER;
  const homePath = isLandlord ? 'properties' : 'tenancies';

  const profileTypeDependentRoutes = isLandlord ? (
    <>
      <Route path="properties" element={<PropertiesLayout />}>
        <Route index element={<PropertiesList />} />
        <Route path=":propertyUid" element={<PropertiesViewLayout />}>
          <Route path="main/*" element={<PropertiesView />}>
            <Route path="tenancies" element={<PropertiesViewTenancies />} />
            <Route path="manage" element={<PropertiesViewManage />} />
            <Route path="*" element={<Navigate to="tenancies" replace />} />
          </Route>
          <Route path="edit" element={<PropertiesCreate />} />
          <Route path="todo" element={<PropertiesViewToDo />} />
          <Route
            path="tenancy/:tenancyUid/*"
            element={<PropertiesViewTenancyLayout />}
          >
            <Route path="main" element={<PropertiesViewTenancy />}>
              <Route
                path="details"
                element={<PropertiesViewTenancyDetails />}
              />
              <Route
                path="payments"
                element={<PropertiesViewTenancyPayments />}
              />
              <Route
                path="notices"
                element={<PropertiesViewTenancyNotices />}
              />
              <Route path="*" element={<Navigate to="details" replace />} />
            </Route>
            <Route path="todo" element={<PropertiesViewTenancyToDo />} />
            <Route path="*" element={<Navigate to="main" replace />} />
          </Route>
          <Route path="tenancy/new" element={<PropertiesCreateTenancy />} />
          <Route path="*" element={<Navigate to="main" replace />} />
        </Route>
        <Route path="new" element={<PropertiesCreate />} />
      </Route>
      <Route path="banking/*" element={<PaymentsHome />}>
        <Route
          path="bank"
          element={
            <Modal onClose={() => navigate(-1)}>
              <PaymentsEditBankAccount />
            </Modal>
          }
        />
      </Route>
      <Route path="subscription" element={<SubscriptionHome />} />
      {publicAccount.data.verificationStatus ===
      verificationStatusTypes.NOT_COMPLETED ? (
        <Route path="verification" element={<LandlordVerification />} />
      ) : null}
    </>
  ) : (
    <>
      <Route path="tenancies" element={<TenanciesLayout />}>
        <Route index element={<TenanciesList />} />
        <Route path="past" element={<TenanciesListPast />} />
        <Route path="view/:tenancyUid" element={<TenanciesViewLayout />}>
          <Route path="main" element={<TenanciesView />}>
            <Route path="details" element={<TenanciesViewDetails />} />
            <Route path="payments" element={<TenanciesViewPayments />} />
            <Route path="notices" element={<TenanciesViewNotices />} />
            <Route path="*" element={<Navigate to="details" replace />} />
          </Route>
          <Route path="todo" element={<TenanciesViewTodo />} />
          <Route path="*" element={<Navigate to="main" replace />} />
        </Route>
      </Route>
      <Route path="payments" element={<Payments />} />
    </>
  );

  return (
    <Routes>
      <Route path="*" element={<Layout />}>
        <Route path="profile" element={<Profile />} />
        <Route path="search/*" element={<Search />} />
        <Route path="notices/*" element={<NoticesLayout />}>
          <Route path="admin" element={<NoticesAdmin />} />
          <Route path="payments" element={<NoticesPayments />} />
          <Route path="messaging" element={<NoticesMessaging />} />
          <Route path="*" element={<Navigate to="admin" replace />} />
        </Route>
        {profileTypeDependentRoutes}
        <Route path="*" element={<Navigate to={homePath} replace />} />
      </Route>
    </Routes>
  );
});

const Navigation = () => {
  const rootState = useRootState();
  // const location = useLocation();

  return (
    <AuthAppSwitch
      appComponent={MainApp}
      authComponent={AuthApp}
      isAuth={rootState.isAuthorized}
    />
  );
};

export default observer(Navigation);
